import React from 'react';
import { Figure } from './Figure';

export type CompareFigureProps = {
  label: string;
  /** The live (Custom) value. */
  value: React.ReactNode;
  /** The Current counterpart. Absent ⇒ this is exactly a plain Figure. */
  current?: React.ReactNode;
  unit?: string;
  hint?: React.ReactNode;
  tone?: 'default' | 'brand' | 'flash';
  loading?: boolean;
};

/** Current-vs-Custom pair — the V2 analogue of classic's morganite ValuePair
 *  (components/ValuePair.jsx), shared by the Pricing section's Solution
 *  figures (exec pricing, Ruling P8) and the live rail's tiles (CONF-212
 *  metric overrides). The Custom side's brand tone is unconditional: "the
 *  whole column is the custom figure whether or not this particular metric
 *  moved" (ValuePair.jsx), so `tone` only governs the single-value case.
 *  Figure itself stays a single-value primitive. */
export function CompareFigure({ label, value, current, unit, hint, tone, loading }: CompareFigureProps) {
  if (current === undefined) {
    return <Figure label={label} value={value} unit={unit} hint={hint} tone={tone} loading={loading} />;
  }
  const unitEl = unit ? <span className="ml-1 font-body text-xs text-ink-muted">{unit}</span> : null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-body text-xs text-ink-muted">{label}</span>
      {loading
        ? <span data-testid="figure-skeleton" className="h-5 w-20 animate-pulse rounded-[var(--radius-2)] bg-surface-high" />
        : (
          <div className="flex flex-col gap-0.5">
            <div>
              <span className="block font-body text-tiny text-ink-muted">Current</span>
              <span className="font-code text-base leading-tight text-ink">{current}</span>{unitEl}
            </div>
            <div>
              <span className="block font-body text-tiny text-brand-strong">Custom</span>
              <span className="font-code text-base leading-tight text-brand-strong">{value}</span>{unitEl}
            </div>
          </div>
        )}
      {hint && !loading && <span className="font-body text-xs text-ink-muted">{hint}</span>}
    </div>
  );
}
